type DataLayerEvent = Record<string, unknown>;

type AnalyticsWindow = Window & {
  dataLayer?: DataLayerEvent[];
};

const isBrowser = typeof window !== 'undefined';

const getAnalyticsWindow = (): AnalyticsWindow | null => {
  if (!isBrowser) {
    return null;
  }

  const w = window as AnalyticsWindow;

  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }

  return w;
};

const pushToDataLayer = (event: DataLayerEvent): void => {
  const w = getAnalyticsWindow();

  if (!w || !Array.isArray(w.dataLayer)) {
    return;
  }

  w.dataLayer.push(event);
};

export type SignUpMethod = 'email' | 'google' | 'facebook' | 'apple';

type TrackSignUpParams = {
  email: string;
  method: SignUpMethod;
};

export const trackSignUp = ({ email, method }: TrackSignUpParams): void => {
  pushToDataLayer({
    event: 'sign_up',
    user_data: {
      email,
    },
    method,
  });
};

type TrackStartTrialParams = {
  email: string;
  planName: string;
};

export const trackStartTrial = ({
  email,
  planName,
}: TrackStartTrialParams): void => {
  pushToDataLayer({
    event: 'start_trial',
    user_data: {
      email,
    },
    plan_name: planName,
  });
};

type BillingInterval = 'yearly' | 'monthly';

type TrackViewPricingParams = {
  billingInterval: BillingInterval;
};

const viewedPricingIntervals = new Set<BillingInterval>();

type TrackPurchaseParams = {
  email: string;
  transactionId: string;
  value: number;
  currency: string;
  planName: string;
  billingInterval: BillingInterval;
  memberCount: number;
};

export const trackPurchase = ({
  email,
  transactionId,
  value,
  currency,
  planName,
  billingInterval,
  memberCount,
}: TrackPurchaseParams): void => {
  pushToDataLayer({
    event: 'purchase',
    user_data: {
      email,
    },
    ecommerce: {
      transaction_id: transactionId,
      value,
      currency,
    },
    plan_name: planName,
    billing_interval: billingInterval,
    member_count: memberCount,
  });
};

export const trackViewPricing = ({
  billingInterval,
}: TrackViewPricingParams): void => {
  if (viewedPricingIntervals.has(billingInterval)) return;

  viewedPricingIntervals.add(billingInterval);
  pushToDataLayer({
    event: 'view_pricing',
    billing_interval: billingInterval,
  });
};
